import { Modal } from '@/components/ui/Modal'
import { useSessionStore } from '@/stores/sessionStore'
import { mockClients } from '@/data'
import { 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Target, 
  CheckCircle, 
  MessageSquare,
  Star,
  Edit,
  Trash2,
  Video
} from 'lucide-react'
import type { Session } from '@/types'

export function SessionDetailModal() {
  const { 
    isSessionDetailModalOpen, 
    closeSessionDetailModal, 
    selectedSession,
    openEditSessionModal,
    deleteSession
  } = useSessionStore()

  if (!selectedSession) return null

  const client = mockClients.find(c => c.id === selectedSession.clientId)

  const getStatusBadge = (status: Session['status']) => {
    const baseClasses = "px-3 py-1 rounded-full text-sm font-medium"
    
    switch (status) {
      case 'scheduled':
        return `${baseClasses} bg-blue-100 text-blue-800`
      case 'in-progress':
        return `${baseClasses} bg-yellow-100 text-yellow-800`
      case 'completed':
        return `${baseClasses} bg-green-100 text-green-800`
      case 'cancelled':
        return `${baseClasses} bg-red-100 text-red-800`
      case 'no-show':
        return `${baseClasses} bg-gray-100 text-gray-800`
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`
    }
  }

  const getLocationIcon = (type: string) => {
    switch (type) {
      case 'virtual':
        return <Video className="w-4 h-4" />
      case 'gym':
        return <MapPin className="w-4 h-4" />
      case 'home':
        return <MapPin className="w-4 h-4" />
      default:
        return <MapPin className="w-4 h-4" />
    }
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date)
  }

  const handleEdit = () => {
    closeSessionDetailModal()
    openEditSessionModal(selectedSession)
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this session?')) {
      deleteSession(selectedSession.id)
    }
  }

  return (
    <Modal
      isOpen={isSessionDetailModalOpen}
      onClose={closeSessionDetailModal}
      title="Session Details"
      size="lg"
    >
      <div className="space-y-6">
        {/* Header with actions */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-xl font-semibold text-gray-900 capitalize">
                {selectedSession.type} Session
              </h3>
              <span className={getStatusBadge(selectedSession.status)}>
                {selectedSession.status.replace('-', ' ')}
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <User className="w-4 h-4" />
              <span>{client?.name || 'Unknown Client'}</span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleEdit}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Edit Session"
            >
              <Edit className="w-5 h-5" />
            </button>
            <button
              onClick={handleDelete}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete Session"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Session Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Date & Time */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Date</p>
                <p className="text-gray-600">{formatDate(selectedSession.date)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Time</p>
                <p className="text-gray-600">
                  {selectedSession.startTime} - {selectedSession.endTime}
                  <span className="text-sm text-gray-500 ml-2">
                    ({selectedSession.duration} min)
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="space-y-4">
            {selectedSession.location && (
              <div className="flex items-start gap-3">
                {getLocationIcon(selectedSession.location.type)}
                <div>
                  <p className="font-medium text-gray-900">Location</p>
                  <p className="text-gray-600 capitalize">{selectedSession.location.type}</p>
                  {selectedSession.location.name && (
                    <p className="text-sm text-gray-500">{selectedSession.location.name}</p>
                  )}
                  {selectedSession.location.address && (
                    <p className="text-sm text-gray-500">{selectedSession.location.address}</p>
                  )}
                  {selectedSession.location.notes && (
                    <p className="text-sm text-gray-500 italic">{selectedSession.location.notes}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Agenda */}
        {selectedSession.agenda && selectedSession.agenda.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Session Agenda
            </h4>
            <ul className="space-y-2">
              {selectedSession.agenda.map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Goals */}
        {selectedSession.goals && selectedSession.goals.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Session Goals
            </h4>
            <ul className="space-y-2">
              {selectedSession.goals.map((goal, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Target className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{goal}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pre-session Notes */}
        {selectedSession.preSessionNotes && (
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Pre-Session Notes
            </h4>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700">{selectedSession.preSessionNotes}</p>
            </div>
          </div>
        )}

        {/* Post-session Content (for completed sessions) */}
        {selectedSession.status === 'completed' && (
          <>
            {/* Post-session Notes */}
            {selectedSession.postSessionNotes && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Post-Session Notes
                </h4>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-gray-700">{selectedSession.postSessionNotes}</p>
                </div>
              </div>
            )}

            {/* Client Feedback */}
            {selectedSession.clientFeedback && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  Client Feedback
                </h4>
                <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Overall Rating</p>
                      <div className="flex justify-center mt-1">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-4 h-4 ${
                              i < selectedSession.clientFeedback!.rating 
                                ? 'text-yellow-400 fill-current' 
                                : 'text-gray-300'
                            }`} 
                          />
                        ))}
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Difficulty</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedSession.clientFeedback.difficulty}/10
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Enjoyment</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedSession.clientFeedback.enjoyment}/10
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Energy</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedSession.clientFeedback.energy}/10
                      </p>
                    </div>
                  </div>
                  
                  {selectedSession.clientFeedback.comments && (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Comments:</p>
                      <p className="text-gray-700 italic">"{selectedSession.clientFeedback.comments}"</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Follow-up Actions */}
            {selectedSession.followUpActions && selectedSession.followUpActions.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Follow-up Actions</h4>
                <ul className="space-y-2">
                  {selectedSession.followUpActions.map((action, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}